import AsyncStorage from '@react-native-community/async-storage';

import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';

import * as reportcacheActions from './reportcache.action';
import { logActivityAsync } from '../../../store/reducers/activity/activity.action';

import { errorLogger } from '../../../utils/errorLogger';
import i18n from 'i18next';

// eslint-disable-next-line camelcase
import { DEPRECATED_cleanupBackupData } from '../../../utils/debugging';
import { isExpiredReport, cleanUpExpiredReports, cleanReportSectionPhotos } from '../../../utils/reportcache';

import { addPendingMulti } from '../reportsubmit';

// Typescript
import { ThunkResult } from '../../../typing/types';
import * as Classes from '../../../utils/classes';
import { currentPeriodStartDate } from '../../../utils/schedule';
import moment from 'moment';
import { ErrorCode } from '../../../helpers/customError';
import { Activity, enums } from '@nimbly-technologies/nimbly-common';
import { appversion } from '../../../constants/appversion';
import API from '../../helpers/api';
import { NIMBLY_API_CHECKIN_REPORT, NIMBLY_API_REPORT, NIMBLY_API_REPORT_DELETE } from '../../../constants/api';

import { selectProfile } from '../../selectors/profile';
const normalizeReportQuestions = (questions?: Classes.Question[]) => {
  if (!Array.isArray(questions)) {
    return [];
  }

  return questions.map(question => {
    const normalizedQuestion = Object.assign({}, question);

    if (normalizedQuestion.type === 'competitor' && !normalizedQuestion.competitorAnalysis) {
      normalizedQuestion.competitorAnalysis = new Classes.CompetitorAnalysisQuestion();
    }

    return normalizedQuestion;
  });
};

const extractHydratedReport = (response: any): Partial<Classes.Report> | null => {
  if (response?.data?.report) {
    return response.data.report;
  }

  if (response?.report) {
    return response.report;
  }

  if (response?.data?.data?.report) {
    return response.data.data.report;
  }

  return null;
};

const extractCheckInPayload = (response: any): any => {
  if (response?.data?.data) {
    return response.data.data;
  }

  if (response?.data) {
    return response.data;
  }

  return response;
};

const mergeHydratedReport = (
  fallbackReport: Classes.Report,
  hydratedReport: Partial<Classes.Report>,
  apiReportID: string
): Classes.Report => {
  const mergedReport: Classes.Report = {
    ...fallbackReport,
    ...hydratedReport,
    apiReportID,
    questions: normalizeReportQuestions(hydratedReport.questions || fallbackReport.questions),
    emailTargets: hydratedReport.emailTargets ?? fallbackReport.emailTargets,
    signatures: hydratedReport.signatures ?? fallbackReport.signatures,
    selfieSignatures: hydratedReport.selfieSignatures ?? fallbackReport.selfieSignatures,
    scheduleKey: hydratedReport.scheduleKey || fallbackReport.scheduleKey,
    hasDeadline: hydratedReport.hasDeadline ?? fallbackReport.hasDeadline,
    startTime: hydratedReport.startTime ?? fallbackReport.startTime,
    endTime: hydratedReport.endTime ?? fallbackReport.endTime,
    datetimeScheduled: hydratedReport.datetimeScheduled || fallbackReport.datetimeScheduled,
    questionnaire: hydratedReport.questionnaire || fallbackReport.questionnaire
  };

  return mergedReport;
};

const mergeHydratedSummary = (
  fallbackSummary: Classes.ReportSummary,
  hydratedReport: Classes.Report
): Classes.ReportSummary => ({
  ...fallbackSummary,
  questionnaire: hydratedReport.questionnaire || fallbackSummary.questionnaire,
  status: hydratedReport.status === 'complete' ? 'complete' : fallbackSummary.status,
  datetimeIn: hydratedReport.datetimeIn || fallbackSummary.datetimeIn,
  datetimeOut: hydratedReport.datetimeOut || fallbackSummary.datetimeOut,
  datetimeUpdated: hydratedReport.datetimeUpdated || fallbackSummary.datetimeUpdated,
  datetimeScheduled: hydratedReport.datetimeScheduled || fallbackSummary.datetimeScheduled,
  isAdhoc: hydratedReport.isAdhoc ?? fallbackSummary.isAdhoc,
  scheduleKey: hydratedReport.scheduleKey || fallbackSummary.scheduleKey
});

const mergeServerSummary = (
  fallbackSummary: Classes.ReportSummary,
  serverSummary: Partial<Classes.ReportSummary> | undefined,
  hydratedReport: Classes.Report
): Classes.ReportSummary => ({
  ...mergeHydratedSummary(fallbackSummary, hydratedReport),
  ...(serverSummary || {})
});

const toLoggedError = (error: unknown): Error => (error instanceof Error ? error : new Error(String(error)));

export const updateCacheReport =
  (siteKey: string, reportKey: string, report: Classes.Report): ThunkResult =>
  async (dispatch, getState) => {
    const { firebase } = getState();

    try {
      return dispatch(reportcacheActions.setCacheReport(siteKey, reportKey, report));
    } catch (error) {
      if (error && firebase.auth && firebase.auth.uid && selectProfile(getState())?.organization) {
        errorLogger(
          firebase.auth.uid,
          selectProfile(getState()).organization,
          siteKey,
          reportKey,
          toLoggedError(error),
          'reportcache/updateCacheReport'
        );
      }
    }
  };

export const updateCacheReportSummary =
  (siteKey: string, reportKey: string, reportSummary: Classes.ReportSummary): ThunkResult =>
  async (dispatch, getState) => {
    const { firebase } = getState();

    try {
      dispatch(reportcacheActions.setCacheReportSummary(siteKey, reportKey, reportSummary));
    } catch (error) {
      if (error && firebase.auth && firebase.auth.uid && selectProfile(getState())?.organization) {
        errorLogger(
          firebase.auth.uid,
          selectProfile(getState()).organization,
          siteKey,
          reportKey,
          toLoggedError(error),
          'reportcache/updateCacheReportSummary'
        );
      }
    }
  };

export const removeReport =
  (siteKey: string, reportKey: string, backup: boolean): ThunkResult =>
  async (dispatch, getState) => {
    const { firebase, reportcache } = getState();
    const { reports, summaries } = reportcache;
    try {
      const selectedReport = reports[siteKey] && reports[siteKey][reportKey];
      const selectedSummary = summaries[siteKey] && summaries[siteKey][reportKey];
      if (backup && selectedReport && selectedSummary) {
        dispatch(reportcacheActions.setBackupReport(reportKey, selectedReport, selectedSummary));
      }
      return dispatch(reportcacheActions.removeCacheReportAndSummary(siteKey, reportKey));
    } catch (error) {
      if (error && firebase.auth && firebase.auth.uid && selectProfile(getState())?.organization) {
        errorLogger(
          firebase.auth.uid,
          selectProfile(getState()).organization,
          siteKey,
          reportKey,
          toLoggedError(error),
          'reportcache/removeUploadedReport'
        );
      }
    }
  };

export const setReportSection =
  (siteKey: string, reportKey: string, sectionIdx: number, report: Classes.ReportSection): ThunkResult =>
  async (dispatch, getState) => {
    try {
      return dispatch(reportcacheActions.setReportSectionState(siteKey, reportKey, sectionIdx, report));
    } catch (error) {
      if (error) {
        const state = getState();
        const { auth, profile } = state.firebase;
        errorLogger(
          auth.uid,
          profile.organization,
          siteKey,
          reportKey,
          toLoggedError(error),
          'reportcache.ts/setReportSection'
        );
      }
    }
  };

export const removeSiteReportSections =
  (siteKey: string, reportKey: string, backup?: boolean): ThunkResult =>
  async (dispatch, getState) => {
    const {
      reportcache: { multiReports: reportSections },
      firebase
    } = getState();
    try {
      if (!backup && reportSections && reportSections[siteKey] && reportSections[siteKey][reportKey]) {
        await cleanReportSectionPhotos(reportSections[siteKey][reportKey]);
      }
      // const keys = await AsyncStorage.getAllKeys();
      // const filteredKeys = keys.filter(key => key.includes(`reportSection/${siteKey}/${reportKey}`));
      // await AsyncStorage.multiRemove(filteredKeys);
      // TODO: Handle backup
      // await backupReportSection(siteKey, reportKey);
      dispatch(reportcacheActions.removeSiteReportSectionState(siteKey));
    } catch (error) {
      if (error && firebase.auth && firebase.auth.uid && selectProfile(getState())?.organization) {
        errorLogger(
          firebase.auth.uid,
          selectProfile(getState()).organization,
          '',
          '',
          toLoggedError(error),
          'reportcache/removeSiteReportSections'
        );
      }
    }
  };

export const removeReportSection =
  (siteKey: string, reportKey: string, sectionIdx: number): ThunkResult =>
  async (dispatch, getState) => {
    const {
      reportcache: { multiReports: reportSections },
      firebase
    } = getState();
    try {
      await cleanReportSectionPhotos(reportSections[siteKey][reportKey], sectionIdx);
      dispatch(reportcacheActions.removeReportSectionState(siteKey, reportKey, sectionIdx));

      return;
    } catch (error) {
      if (error && firebase.auth && firebase.auth.uid && selectProfile(getState())?.organization) {
        errorLogger(
          firebase.auth.uid,
          selectProfile(getState()).organization,
          '',
          '',
          toLoggedError(error),
          'reportcache/removeReportSection'
        );
      }
    }
  };

export const removeBackupCache =
  (keys: string[]): ThunkResult =>
  async (dispatch, getState) => {
    const { firebase } = getState();
    try {
      const backupCaches = await AsyncStorage.multiGet(keys);

      const parsedData: [string, Classes.Report | Classes.ReportSection | Classes.ReportSummary | null][] = [];

      backupCaches.forEach(([key, valueStr]) => {
        const [backup, type, siteKey, date, sectionIdx] = key.split('/');
        if (backup === 'backup') {
          if (valueStr && valueStr !== '__invalid__') {
            const value = JSON.parse(valueStr);
            parsedData.push([key, value]);
          } else if (valueStr === '__invalid__') {
            parsedData.push([key, null]);
          } else {
            parsedData.push([key, null]);
          }
        }
      });

      await DEPRECATED_cleanupBackupData(parsedData, false);

      return;
    } catch (error) {
      if (error && firebase.auth && firebase.auth.uid && selectProfile(getState())?.organization) {
        errorLogger(
          firebase.auth.uid,
          selectProfile(getState()).organization,
          '',
          '',
          toLoggedError(error),
          'reportcache/removeBackupCache'
        );
      }
    }
  };

// Updates or sets the report in the cache
export const upsertCacheReport =
  (siteKey: string, reportKey: string, report: Classes.Report, reportSummary: Classes.ReportSummary): ThunkResult =>
  async (dispatch, getState) => {
    const { firebase } = getState();

    try {
      return dispatch(reportcacheActions.setCacheReportAndSummary(siteKey, reportKey, report, reportSummary));
    } catch (error) {
      if (error && firebase.auth && firebase.auth.uid && selectProfile(getState())?.organization) {
        errorLogger(
          firebase.auth.uid,
          selectProfile(getState()).organization,
          siteKey,
          reportKey,
          toLoggedError(error),
          'reportcache/upsertCacheReport'
        );
      }
    }
  };

export type CacheReport = {
  cacheKey: string;
  type: 'report' | 'reportIndex' | 'reportSection' | 'unavailable';
  siteKey: string;
  reportKey: string;
  value: Classes.Report | Classes.ReportSummary | Classes.ReportSection;
  isExpired: boolean | null;
  sectionIdx?: number;
};

// eslint-disable-next-line camelcase
export const DEPRECATED_getReportsFromCache =
  (organizationSchedule: Classes.OrganizationSchedule): ThunkResult =>
  async (dispatch, getState) => {
    const { firebase } = getState();

    try {
      const keys = await AsyncStorage.getAllKeys();
      const reportKeyReg = /report-|report\//i;
      const reportSummaryKeyReg = /reportIndex-|reportIndex\//i;
      const reportSectionReg = `reportSection/`;

      const cacheReportKeys: string[] = [];
      const summariesKeys: string[] = [];
      const multiReportKeys: string[] = [];
      const backupKeys: string[] = [];

      for (const k of keys) {
        if (k.includes('backup/')) {
          backupKeys.push(k);
        } else if (reportSummaryKeyReg.test(k)) {
          summariesKeys.push(k);
        } else if (k.includes(reportSectionReg)) {
          multiReportKeys.push(k);
        } else if (reportKeyReg.test(k)) {
          cacheReportKeys.push(k);
        }
      }

      if (backupKeys.length > 0) {
        await dispatch(removeBackupCache(backupKeys));
      }
      const mergedKeys = [...cacheReportKeys, ...summariesKeys, ...multiReportKeys];
      if (mergedKeys.length) {
        const cacheItems = await AsyncStorage.multiGet(mergedKeys);

        const parsedCacheReports = cacheItems.map(([key, valueStr]) => {
          const isReport = reportKeyReg.test(key);
          const isReportIndex = reportSummaryKeyReg.test(key);
          const isReportSection = key.includes('reportSection/');

          const value = JSON.parse(valueStr!);

          let cacheKey;
          let reportKey;
          let cacheSiteKey;
          let sectionIdx;
          if (!isReportSection) {
            /**
             * Format `report/{siteKey}/{reportKey}`
             */
            const keySplit = key.split('/');
            cacheKey = isReport ? 'report/' : isReportIndex ? 'reportIndex/' : '';
            reportKey = /report-|reportIndex-/i.test(key) ? key.substring(key.length - 10) : keySplit[2];
            cacheSiteKey = /report-|reportIndex-/i.test(key)
              ? key.substring(cacheKey.length, key.length - 11)
              : keySplit[1];
          } else {
            [cacheKey, cacheSiteKey, reportKey, sectionIdx] = key.split('/');
          }

          // TODO: RESTU: may need better handling for expiring multi report
          const isExpired = isExpiredReport(value, organizationSchedule);

          const cacheReport: CacheReport = {
            cacheKey,
            type: isReport
              ? 'report'
              : isReportIndex
              ? 'reportIndex'
              : isReportSection
              ? 'reportSection'
              : 'unavailable',
            siteKey: cacheSiteKey,
            reportKey: reportKey,
            value,
            sectionIdx: Number(sectionIdx),
            isExpired
          };
          return cacheReport;
        });

        const singleSite: CacheReport[] = [];
        const multiSite: CacheReport[] = [];
        const expiredReports: CacheReport[] = [];

        const cleanupInvalidCache: string[] = [];

        for (const report of parsedCacheReports) {
          if (report.type === 'unavailable' || !report.value) {
            cleanupInvalidCache.push(report.cacheKey);
            continue;
          }
          if (report.type === 'reportSection' && !report.isExpired) {
            multiSite.push(report);
          } else if (report.type !== 'reportSection' && !report.isExpired) {
            singleSite.push(report);
          } else if (report.isExpired) {
            expiredReports.push(report);
          }
        }

        if (cleanupInvalidCache.length > 0) {
          await AsyncStorage.multiRemove(cleanupInvalidCache);
        }

        dispatch(setCacheItems(singleSite));
        dispatch(setMultiCacheItems(multiSite));
        dispatch(reportcacheActions.setCacheReady());

        if (expiredReports.length > 0) {
          await cleanUpExpiredReports(expiredReports);
        }
      }
      dispatch(reportcacheActions.setCacheReady());
    } catch (error) {
      if (error && firebase.auth && firebase.auth.uid && selectProfile(getState())?.organization) {
        errorLogger(
          firebase.auth.uid,
          selectProfile(getState()).organization,
          '',
          '',
          toLoggedError(error),
          'reportcache/getReportFromCache'
        );
      }
      dispatch(reportcacheActions.setCacheReady());
      // Do nothing
    }
  };

export const setCacheItems =
  (cacheItems: CacheReport[]): ThunkResult =>
  dispatch => {
    cacheItems
      .filter(({ isExpired, type }) => !isExpired && type !== 'unavailable')
      .forEach(({ type, value, siteKey, reportKey }) => {
        if (type === 'report') {
          dispatch(reportcacheActions.setCacheReport(siteKey, reportKey, value as Classes.Report));
          if (value.status === 'complete' && value.datetimeOut && value.datetimeOut.length > 0) {
            dispatch(addPendingMulti(siteKey, reportKey));
          }
        } else {
          dispatch(reportcacheActions.setCacheReportSummary(siteKey, reportKey, value as Classes.ReportSummary));
        }
      });

    return Promise.resolve();
  };

export const setMultiCacheItems =
  (multiCacheItems: CacheReport[]): ThunkResult =>
  dispatch => {
    multiCacheItems
      .filter(({ isExpired, type }) => !isExpired && type !== 'unavailable')
      .map(({ value, siteKey, reportKey: date, sectionIdx }) => {
        dispatch(reportcacheActions.setReportSectionState(siteKey, date, sectionIdx!, value as Classes.ReportSection));
      });

    return Promise.resolve();
  };

// new implementation
export const cleanLocalExpiredReports =
  (organizationSchedule: Classes.OrganizationSchedule): ThunkResult =>
  (dispatch, getState) => {
    const startDate = currentPeriodStartDate(organizationSchedule);

    if (!startDate) {
      return;
    }
    const state = getState();

    const { reportcache } = state;
    const { profile } = state.firebase;
    const { reports, summaries, multiReports, multiSummaries, backupReports, backupSummaries } = reportcache;

    if (!profile || profile.isEmpty) {
      return;
    }

    if (isEmpty(reports) || isEmpty(summaries)) {
      dispatch(reportcacheActions.setCacheReady());

      return;
    }

    const newReports = cloneDeep(reports);
    const newSummaries = cloneDeep(summaries);
    const newMultiReports = cloneDeep(multiReports);
    const newMultiSummaries = cloneDeep(multiSummaries);

    // merge summary and report to get consistent value
    const clonedSummaries = cloneDeep(summaries);
    const clonedMultiSummaries = cloneDeep(multiSummaries);
    const mergedReport = merge(newReports, clonedSummaries);
    const mergedMultiReports = merge(multiReports, clonedMultiSummaries);

    // TODO: Confirm start need to be added by 4 hours or not

    const prevEndLimit = startDate.add(4, 'hours');
    Object.keys(mergedReport).forEach(siteKey => {
      const siteReports = mergedReport[siteKey];
      Object.keys(siteReports || {}).forEach(reportKey => {
        const currentReport = siteReports[reportKey];

        const selectedDate =
          currentReport.datetimeIn || currentReport.datetimeUpdated || currentReport.datetimeScheduled;
        if (moment(selectedDate).isBefore(prevEndLimit)) {
          delete newReports[siteKey][reportKey];
          delete newSummaries[siteKey][reportKey];
        }
      });

      if (isEmpty(newReports[siteKey]) || isEmpty(newSummaries[siteKey])) {
        delete newReports[siteKey];
        delete newSummaries[siteKey];
      }
    });

    Object.keys(mergedMultiReports || {}).forEach(siteKey => {
      const siteReports = mergedMultiReports[siteKey];
      Object.keys(siteReports || {}).forEach(reportKey => {
        const reportSections = siteReports[reportKey];
        for (const sectionIdx of Object.keys(reportSections || {})) {
          const currentSection = reportSections[sectionIdx];
          const selectedDate =
            currentSection.datetimeIn || currentSection.datetimeUpdated || currentSection.datetimeScheduled;
          if (moment(selectedDate).isBefore(prevEndLimit)) {
            if (!Object.keys(newMultiReports).length) return;
            delete newMultiReports[siteKey][reportKey];

            if (!Object.keys(newMultiSummaries).length) return;
            delete newMultiSummaries[siteKey][reportKey];
            break;
          }
        }
      });
      if (isEmpty(newMultiReports[siteKey])) {
        delete newMultiReports[siteKey];
        delete newMultiSummaries[siteKey];
      }
    });

    const expiredBackupCaches: string[] = [];
    // merge summary and report to get consistent value
    const clonedBackupReports = cloneDeep(backupReports);
    const clonedBackupSummaries = cloneDeep(backupSummaries);
    const mergedBackup = merge(clonedBackupSummaries, clonedBackupReports);

    for (const reportKey of Object.keys(mergedBackup || {})) {
      const [date, unixString] = reportKey.split('_');
      // remove backup that's more than 3 days old
      if (moment().diff(unixString ? Number(unixString) : date, 'day') > 3) {
        expiredBackupCaches.push(reportKey);
      }
    }

    if (expiredBackupCaches.length) {
      dispatch(reportcacheActions.removeBackupReports(expiredBackupCaches));
    }

    dispatch(reportcacheActions.initReportCache(newReports, newSummaries, newMultiReports, newMultiSummaries));
    dispatch(reportcacheActions.setCacheReady());
  };

export const removeDraftReport =
  (siteKey: string, reportKey: string): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState();

    const { network } = state;
    const { profile, auth } = state.firebase;

    const organizationKey = profile.organization;
    const language: Classes.Language = profile.language || 'en';

    if (!network.isConnected) {
      throw new ErrorCode('no-internet', i18n.t('saga:reportCache.noConnection'));
    }

    if (!reportKey) {
      errorLogger(
        auth.uid,
        organizationKey,
        siteKey,
        reportKey,
        new Error(i18n.t('saga:reportCache.noKeyReport')),
        'reportcache.actionThunk.ts/removeDraftReport'
      );

      throw new ErrorCode('unknown-error', i18n.t('saga:reportCache.systemError'));
    }

    try {
      const cachedReport = state.reportcache.reports[siteKey]?.[reportKey];
      const apiReportID = cachedReport?.apiReportID || reportKey;
      const deleteUrl = `${NIMBLY_API_REPORT_DELETE}/${siteKey}/${apiReportID}`;

      const api = await API.withToken();
      await api.deleteJSON(deleteUrl);
      dispatch(reportcacheActions.removeCacheReportAndSummary(siteKey, reportKey));
    } catch (error) {
      errorLogger(
        auth.uid,
        organizationKey,
        siteKey,
        reportKey,
        toLoggedError(error),
        'reportcache.actionThunk.ts/removeDraftReport'
      );
      // Keep local cache on API failure
      throw new ErrorCode('internal-server', i18n.t('saga:reportCache.systemError'));
    }
  };

export const createOrUpdateReport =
  (
    siteKey: string,
    reportKey: string,
    report: Classes.Report,
    summary: Classes.ReportSummary
  ): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    try {
      dispatch(reportcacheActions.setCacheReportAndSummary(siteKey, reportKey, report, summary));
    } catch (error) {
      console.log('error', error);
      throw new ErrorCode('internal-server', i18n.t('saga:reportCache.systemError'));
    }
  };

/**
 * updateReport — write an existing draft report to Firebase (non-check-in updates).
 * Mirrors the existing createOrUpdateReport Firebase logic.
 */
export const updateReport =
  (
    siteKey: string,
    reportKey: string,
    report: Classes.Report,
    summary: Classes.ReportSummary
  ): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    try {
      dispatch(reportcacheActions.setCacheReportAndSummary(siteKey, reportKey, report, summary));
    } catch (error) {
      throw new ErrorCode('internal-server', i18n.t('saga:reportCache.systemError'));
    }
  };

export const createReport =
  (
    siteKey: string,
    reportKey: string,
    report: Classes.Report,
    summary: Classes.ReportSummary,
    options?: {
      scheduleDate?: string;
      scheduleType?: string;
      gpsPrecision?: string;
      coordinates?: { latitude: number; longitude: number } | null;
    }
  ): ThunkResult<Promise<string>> =>
  async (dispatch, getState) => {
    const state = getState();
    const { auth, profile } = state.firebase;
    const organizationKey = profile?.organization;

    try {
      const { cacheLocation } = state.geolocation;
      const organization = state.organization.myOrganization;
      const selectedSite = state.site.selectedSite;

      const orgGeoPrecision: string | undefined = organization?.geoPrecision;
      const siteRadius: string | undefined = options?.gpsPrecision || selectedSite?.radius || undefined;
      const effectiveRadius = siteRadius && siteRadius !== 'default' ? siteRadius : orgGeoPrecision;
      const gpsRequired = effectiveRadius !== 'none';
      const explicitCoordinates = options?.coordinates || null;
      const hasCoordinates = Boolean(explicitCoordinates) || cacheLocation.isFetched;

      if (gpsRequired && !hasCoordinates) {
        throw new ErrorCode(
          'gps-unavailable',
          i18n.t('saga:reportCache.gpsUnavailable', 'GPS location is required for check-in but is not available.')
        );
      }

      const coordinates = explicitCoordinates
        ? explicitCoordinates
        : gpsRequired && cacheLocation.isFetched
        ? { latitude: cacheLocation.coords.latitude, longitude: cacheLocation.coords.longitude }
        : { latitude: 0, longitude: 0 };

      const siteSchedules = state.siteschedule?.[siteKey]?.singleSchedules || [];
      const matchingSchedule = siteSchedules.find(s => s.key === report.scheduleKey);
      const scheduleType: string = options?.scheduleType || matchingSchedule?.type || 'standard';
      const scheduleDate = options?.scheduleDate || moment.parseZone(report.datetimeScheduled).format('YYYY-MM-DD');

      const payload = {
        coordinates,
        date: scheduleDate,
        deviceTimeIn: moment().toISOString(),
        scheduleID: report.scheduleKey,
        type: scheduleType,
        metadata: {
          deviceSettings: {
            automaticDateTimeSetting: true,
            isDeviceTimeChanged: false
          },
          offline: {
            isCheckInOffline: !state.network.isConnected,
            isDownloadedScheduleExpired: false
          }
        }
      };

      const api = await API.withToken();
      const response = await api.postJSON(NIMBLY_API_CHECKIN_REPORT, payload);
      const checkInPayload = extractCheckInPayload(response);

      const firebaseReportID = checkInPayload?.firebaseReportID || checkInPayload?.reportID;
      if (typeof firebaseReportID !== 'string' || firebaseReportID.trim().length === 0) {
        throw new ErrorCode('internal-server', i18n.t('saga:reportCache.systemError'));
      }

      const updatedReport: Classes.Report = checkInPayload?.report
        ? mergeHydratedReport(report, checkInPayload.report, firebaseReportID)
        : { ...report, apiReportID: firebaseReportID };
      const updatedSummary: Classes.ReportSummary = mergeServerSummary(summary, checkInPayload?.summary, updatedReport);

      // Store cache under the API-generated key. Remove the old client-generated entry
      // so all subsequent lookups by selectedReportKey resolve to the correct record.
      dispatch(reportcacheActions.setCacheReportAndSummary(siteKey, firebaseReportID, updatedReport, updatedSummary));
      if (firebaseReportID !== reportKey) {
        dispatch(reportcacheActions.removeCacheReportAndSummary(siteKey, reportKey));
      }

      return firebaseReportID;
    } catch (error) {
      errorLogger(
        auth.uid,
        organizationKey,
        siteKey,
        reportKey,
        toLoggedError(error),
        'reportcache.actionThunk.ts/createReport'
      );
      throw error;
    }
  };

/**
 * hydrateLiteReport — load a report's full content (questions + attachments) on entry.
 *
 * Re-fetches from the report API on every entry so a draft edited on another device is
 * picked up. Conflict policy is "server-newer-wins": the freshly fetched copy replaces the
 * local cache only when its `datetimeUpdated` is strictly newer; otherwise the local copy is
 * kept. This means an unsynced local draft is never overwritten by an older/equal server copy.
 *
 * Falls back to the local cached copy (when it has questions) on: offline, API error, or an
 * empty server payload — so continue-draft keeps working without a network round-trip.
 */
export const hydrateLiteReport =
  (
    siteKey: string,
    reportKey: string,
    fallbackReport: Classes.Report,
    fallbackSummary?: Classes.ReportSummary
  ): ThunkResult<Promise<Classes.Report>> =>
  async (dispatch, getState) => {
    const state = getState();
    const { auth, profile } = state.firebase;
    const organizationKey = profile?.organization;
    const cachedReport = state.reportcache?.reports?.[siteKey]?.[reportKey];
    const hasCachedQuestions = Boolean(cachedReport?.questions && cachedReport.questions.length > 0);
    const isConnected = state.network?.isConnected !== false;

    // Returns the local cached copy (re-syncing the summary when provided). Used as the
    // safe fallback whenever the server copy is unavailable or not newer than local.
    const keepCachedReport = (): Classes.Report => {
      if (fallbackSummary && cachedReport) {
        dispatch(
          reportcacheActions.setCacheReportAndSummary(
            siteKey,
            reportKey,
            cachedReport,
            mergeHydratedSummary(fallbackSummary, cachedReport)
          )
        );
      }

      return cachedReport as Classes.Report;
    };

    // Parses an ISO datetime to epoch ms; returns NaN for empty/undefined/invalid values
    // (note: moment(undefined) would default to "now", which we must avoid here).
    const toTime = (value?: string): number => (value ? moment(value).valueOf() : NaN);

    // Offline: cannot reach the server. Keep the local draft if we have usable content.
    if (!isConnected && hasCachedQuestions) {
      return keepCachedReport();
    }

    try {
      const api = await API.withToken();
      const response = await api.getJSONOrThrow(`${NIMBLY_API_REPORT}/${siteKey}/${reportKey}`);
      const extractedReport = extractHydratedReport(response);

      if (!extractedReport) {
        // Server returned no usable report. Prefer keeping the local draft over failing.
        if (hasCachedQuestions) {
          return keepCachedReport();
        }
        throw new ErrorCode('internal-server', i18n.t('saga:reportCache.systemError'));
      }

      // Server-newer-wins: only adopt the server copy when it is strictly newer than the
      // local cached copy. This prevents overwriting local edits that have not synced yet.
      // datetimeUpdated is bumped on every local edit (Questionnaire saveQuestionnaire) and
      // by the server on each /update, so it is the freshness signal. When the server time
      // is missing/unparseable we cannot prove it is newer, so we keep the local copy.
      if (hasCachedQuestions) {
        const serverTime = toTime(extractedReport.datetimeUpdated as string | undefined);
        const localTime = toTime(cachedReport!.datetimeUpdated);
        const serverIsNewer = Number.isFinite(serverTime) && (!Number.isFinite(localTime) || serverTime > localTime);

        if (!serverIsNewer) {
          return keepCachedReport();
        }
      }

      const hydratedReport = mergeHydratedReport(fallbackReport, extractedReport, reportKey);

      if (fallbackSummary) {
        dispatch(
          reportcacheActions.setCacheReportAndSummary(
            siteKey,
            reportKey,
            hydratedReport,
            mergeHydratedSummary(fallbackSummary, hydratedReport)
          )
        );
      } else {
        dispatch(reportcacheActions.setCacheReport(siteKey, reportKey, hydratedReport));
      }

      return hydratedReport;
    } catch (error) {
      // Network/API failure: keep the local draft when available rather than break the flow.
      if (hasCachedQuestions) {
        return keepCachedReport();
      }

      errorLogger(
        auth.uid,
        organizationKey,
        siteKey,
        reportKey,
        toLoggedError(error),
        'reportcache.actionThunk.ts/hydrateLiteReport'
      );
      throw error;
    }
  };
